import * as React from "react";
import { Slot } from "@radix-ui/react-slot";
import { cva, type VariantProps } from "class-variance-authority";

import { cn } from "@uconnect/assets/lib/utils";

const buttonVariants = cva(
	// ring-inset: a Button can sit flush against a scroll viewport's
	// overflow-x-hidden edge (e.g. the admin conversation feed) — an
	// outward-drawn ring gets clipped clean off there on keyboard focus,
	// an inward one never does, at no cost to layout width.
	"inline-flex items-center justify-center gap-2 whitespace-nowrap rounded-md! text-sm font-medium! transition-all disabled:pointer-events-none disabled:opacity-50 [&_svg]:pointer-events-none [&_svg:not([class*='size-'])]:size-4 shrink-0 [&_svg]:shrink-0 outline-none focus-visible:border-ring focus-visible:ring-ring/50 focus-visible:ring-[3px] focus-visible:ring-inset aria-invalid:ring-destructive/20 dark:aria-invalid:ring-destructive/40 aria-invalid:border-destructive cursor-pointer box-border! border-0!",
	{
		variants: {
			variant: {
				default:
					"!bg-primary text-primary-foreground! hover:bg-primary-light! hover:text-primary-foreground! disabled:text-primary-foreground!",
				destructive:
					"bg-destructive! text-white! hover:bg-destructive/90! hover:text-white! focus-visible:ring-destructive/20",
				outline:
					"border! border-1! border-primary! text-primary! bg-background! hover:bg-primary! hover:text-primary-foreground!",
				secondary:
					"bg-secondary! text-secondary-foreground! hover:bg-secondary/80! hover:text-secondary-foreground!",
				link: "text-primary! bg-transparent! underline-offset-4 [&:not(.group)]:hover:underline!",
				ghost: "bg-transparent! text-primary! hover:!bg-navy-100 hover:text-primary!",
				light: "bg-accent! text-accent-foreground! hover:bg-accent/90! hover:text-accent-foreground!",
				mutedInterface:
					"opacity-70 hover:opacity-100 transition-opacity bg-transparent! text-foreground! hover:text-foreground! hover:bg-transparent! border-0! p-0!",
			},
			size: {
				default: "h-9 px-4 py-2 has-[>svg]:px-3",
				xs: "h-7 rounded-md gap-1 px-2 !text-xs",
				sm: "h-8 rounded-md gap-1.5 px-3 has-[>svg]:px-2.5",
				lg: "h-10 rounded-md px-6 has-[>svg]:px-4",
				icon: "size-9 !p-0",
				"icon-sm": "size-8 !p-0",
				"icon-lg": "size-10 !p-0",
			},
		},
		defaultVariants: {
			variant: "default",
			size: "default",
		},
	}
);

const Button = React.forwardRef<
	HTMLButtonElement,
	React.ComponentProps<"button"> &
		VariantProps<typeof buttonVariants> & {
			asChild?: boolean;
		}
>(({
	className,
	variant,
	size,
	asChild = false,
	...props
}, ref) => {
	const Comp = asChild ? Slot : "button";

	return (
		<Comp
			ref={ref}
			data-slot="button"
			className={cn(buttonVariants({ variant, size, className }))}
			{...props}
		/>
	);
});

Button.displayName = "Button";

export { Button, buttonVariants };
